import type { MfgRecipeKind } from "@/lib/manufacturing/types";

export type ComposeEntryMode = "bottom_up" | "top_down";

export type ComposeNodeRole = "product" | "component";

export type ComposeMatchState =
  | { status: "unmatched" }
  | { status: "matched"; recipe_id: string }
  | { status: "theoretical" };

/**
 * Series-common slot label with AI-ready fill descriptions and sharing flags.
 * Example: door · "single wooden shed door" · changesWithSize · Wx12 · "double…"
 */
export type ComposeSlotDef = {
  key: string;
  label: string;
  /** AI-ready description of the standard / at-or-below fill. */
  description: string;
  seriesSpecific: boolean;
  sizeSpecific: boolean;
  styleSpecific: boolean;
  /** When true, pick description vs aboveDescription via thresholdSize. */
  changesWithSize: boolean;
  /** Threshold like 10x12 or Wx12; used when changesWithSize. */
  thresholdSize: string;
  /** AI-ready description when size is above threshold. */
  aboveDescription: string;
};

export type ComposeSlotLink = {
  slotKey: string;
  label: string;
  childNodeId: string | null;
};

export type ComposeNode = {
  id: string;
  role: ComposeNodeRole;
  kind: MfgRecipeKind;
  code: string;
  name: string;
  sizeLabel: string | null;
  styleKey: string | null;
  /** For shared components: which slot template key produced this node. */
  slotKey: string | null;
  /** Predicate variant label / fill slug when changesWithSize. */
  variantKey: string | null;
  tags: string[];
  slots: ComposeSlotLink[];
  match: ComposeMatchState;
};

export type ComposeArchetype = {
  recipe_group_id: string | null;
  seriesName: string;
  seriesCode: string;
  /**
   * Product SKU **code** pattern.
   * Tokens: {CODE}/{GROUP} {SIZE} {SIZE_COMPACT} {SERIES} {STYLE} {STYLE_ABBREV}
   * Legacy alias field name kept for persisted plans.
   */
  namingPattern: string;
  /**
   * Product SKU **name** pattern (same tokens as namingPattern).
   * Default `{SERIES} {SIZE} {STYLE}`.
   */
  namePattern: string;
  sizesRaw: string;
  styles: string[];
  slots: ComposeSlotDef[];
  /** Top-down paste / generated: CODE | Name per line. */
  skuListRaw: string;
  /**
   * Tells AI (and operators) how spoken/written sizes map — default WxL
   * (first number width, second length).
   */
  sizeAxisHint: string;
};

export type ComposePlanStep =
  | "entry"
  | "define"
  | "review"
  | "match"
  | "commit";

export type ComposeConfirmedLink = {
  slotKey: string;
  sizeLabel: string | null;
  styleKey: string | null;
  variantKey: string | null;
  recipe_id: string;
  code: string;
};

export type ComposePlan = {
  version: 1;
  planId: string;
  orgId: string;
  entryMode: ComposeEntryMode;
  step: ComposePlanStep;
  archetype: ComposeArchetype;
  nodes: ComposeNode[];
  /** In-session match boosts: accepted code/name needles. */
  matchBoosts: string[];
  /**
   * Confirmed slot→recipe links from this plan (and prior partial commits).
   * Used to improve suggestions for remaining SKUs.
   */
  confirmedLinks: ComposeConfirmedLink[];
  createdAt: string;
  updatedAt: string;
};

export function emptyComposeSlot(partial?: Partial<ComposeSlotDef>): ComposeSlotDef {
  return {
    key: "",
    label: "",
    description: "",
    seriesSpecific: false,
    sizeSpecific: false,
    styleSpecific: false,
    changesWithSize: false,
    thresholdSize: "",
    aboveDescription: "",
    ...partial,
  };
}

/** One blank starter row — not industry-specific; no flags checked. */
export const DEFAULT_COMPOSE_SLOTS: ComposeSlotDef[] = [
  emptyComposeSlot({
    key: "floor",
    label: "Floor",
    description: "floor",
  }),
];

/** Coerce persisted / AI / legacy slot rows into ComposeSlotDef. */
export function normalizeComposeSlot(raw: unknown): ComposeSlotDef | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  const key = typeof s.key === "string" ? s.key.trim() : "";
  if (!key) return null;
  const label =
    typeof s.label === "string" && s.label.trim() ? s.label.trim() : key;

  const styleSpecific =
    s.styleSpecific === true ||
    s.splitByStyle === true ||
    s.style_specific === true;
  const seriesSpecific = s.seriesSpecific === true || s.series_specific === true;
  const changesWithSize =
    s.changesWithSize === true ||
    s.changes_with_size === true ||
    s.variantFromPredicate === "door" ||
    s.variantFromPredicate === "wall_height";

  let sizeSpecific = false;
  if ("sizeSpecific" in s || "size_specific" in s) {
    sizeSpecific = Boolean(s.sizeSpecific ?? s.size_specific);
  } else if ("sharedBySize" in s) {
    // Legacy: sharedBySize meant one node per size among products.
    sizeSpecific = s.sharedBySize !== false;
  }

  return emptyComposeSlot({
    key,
    label,
    description: typeof s.description === "string" ? s.description : "",
    seriesSpecific,
    sizeSpecific,
    styleSpecific,
    changesWithSize,
    thresholdSize:
      typeof s.thresholdSize === "string"
        ? s.thresholdSize
        : typeof s.threshold_size === "string"
          ? s.threshold_size
          : "",
    aboveDescription:
      typeof s.aboveDescription === "string"
        ? s.aboveDescription
        : typeof s.above_description === "string"
          ? s.above_description
          : "",
  });
}

export function normalizeComposeSlots(raw: unknown): ComposeSlotDef[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_COMPOSE_SLOTS.map((s) => ({ ...s }));
  }
  const out: ComposeSlotDef[] = [];
  for (const item of raw) {
    const n = normalizeComposeSlot(item);
    if (n) out.push(n);
  }
  return out.length ? out : DEFAULT_COMPOSE_SLOTS.map((s) => ({ ...s }));
}

export const DEFAULT_SIZE_AXIS_HINT =
  "Sizes are written WIDTH×LENGTH (W×L). Example: 10x12 means 10 ft wide by 12 ft long — not length×width.";

export function emptyArchetype(partial?: Partial<ComposeArchetype>): ComposeArchetype {
  const { slots: rawSlots, namingPattern, namePattern, ...rest } = partial ?? {};
  return {
    recipe_group_id: null,
    seriesName: "",
    seriesCode: "",
    sizesRaw: "",
    styles: [],
    skuListRaw: "",
    sizeAxisHint: DEFAULT_SIZE_AXIS_HINT,
    ...rest,
    namingPattern:
      typeof namingPattern === "string" && namingPattern.trim()
        ? namingPattern
        : "{CODE}-{SIZE}-{STYLE}",
    namePattern:
      typeof namePattern === "string" && namePattern.trim()
        ? namePattern
        : "{SERIES} {SIZE} {STYLE}",
    slots: normalizeComposeSlots(rawSlots ?? DEFAULT_COMPOSE_SLOTS),
  };
}

export function newComposePlan(input: {
  orgId: string;
  entryMode: ComposeEntryMode;
  archetype?: Partial<ComposeArchetype>;
}): ComposePlan {
  const now = new Date().toISOString();
  return {
    version: 1,
    planId: `compose-${Date.now().toString(36)}`,
    orgId: input.orgId,
    entryMode: input.entryMode,
    step: "define",
    archetype: emptyArchetype(input.archetype),
    nodes: [],
    matchBoosts: [],
    confirmedLinks: [],
    createdAt: now,
    updatedAt: now,
  };
}
